import { useState, useEffect } from "react";
import { Calendar, ConfigProvider, Spin, Select, Row, Col, Card, Typography } from "antd";
import "moment/locale/es";
import locale from "antd/lib/locale/es_ES";
import { User } from "lucide-react";
import { useAuth } from "../../layout/auth";
import axios from "axios";
import moment from "moment";
import "./CalendarView.css";
import { useCalendarData } from "./useCalendarData";
import CalendarHeader from "./CalendarHeader";
import CalendarLegend from "./CalendarLegend";
import CalendarCell from "./CalendarCell";
import ItemDetailsModal from "./ItemDetailsModal";
import DayDetailsModal from "./DayDetailsModal";
import PageTitle from "../../utils/PageTitle";
import { CalendarDays } from "lucide-react";

moment.updateLocale("es", {
  week: {
    dow: 0, // Sunday is the first day of the week
    doy: 1, // First week of year must contain 1st January
  },
});

const customLocale = {
  ...locale,
  Calendar: {
    ...locale.Calendar,
    week: {
      dow: 0,
      doy: 1,
    },
  },
};

export default function CalendarView() {
  const [selectedValue, setSelectedValue] = useState(moment());
  const auth = useAuth();
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState("all");
  const [isMaximized, setIsMaximized] = useState(false);
  const [visibleTypes, setVisibleTypes] = useState([
    "visita",
    "solicitud",
    "contrato",
    "listo",
    "emision",
    "revision1",
    "revision2",
    "papeleria",
    "cancelacion",
  ]);
  const [itemModalVisible, setItemModalVisible] = useState(false);
  const [dayModalVisible, setDayModalVisible] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedDayItems, setSelectedDayItems] = useState([]);

  const { loading, dates } = useCalendarData(selectedValue, selectedUser, auth);

  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };

  const handleLegendClick = (type) => {
    setVisibleTypes((prev) => (prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]));
  };

  const showAllEventTypes = () => {
    setVisibleTypes(["visita", "solicitud", "contrato", "listo", "emision", "revision1", "revision2", "papeleria", "cancelacion"]);
  };

  const hideAllEventTypes = () => {
    setVisibleTypes([]);
  };

  const handleItemClick = (item) => {
    setSelectedItem(item);
    setItemModalVisible(true);
  };

  const handleDayClick = (value) => {
    const dayItems = dates
      ?.filter((date) => moment(date.Fecha).format("YYYY-MM-DD") === value.format("YYYY-MM-DD"))
      .filter((item) => visibleTypes.includes(item.Tipo));

    setSelectedDay(value);
    setSelectedDayItems(dayItems);
    setDayModalVisible(true);
  };

  const onSelect = (value) => {
    // Only show modal if we're selecting a day in the currently visible month
    if (value.format("YYYY-MM") === selectedValue.format("YYYY-MM")) {
      handleDayClick(value);
    }
  };

  const onPanelChange = (value) => {
    setSelectedValue(value);
    setSelectedDay(null);
    setSelectedDayItems([]);
    setDayModalVisible(false);
  };

  useEffect(() => {
    if (auth.Rol === "director") {
      axios
        .get(`${process.env.REACT_APP_API_URL}/usuarios/getUsuarios`)
        .then((response) => {
          setUsers(response.data);
        })
        .catch((err) => {
          console.log(err);
        });
    }
  }, [auth.Rol]);

  return (
    <div className={`calendar-view-container ${isMaximized ? "calendar-maximized" : ""}`}>
      <Card className="calendar-card" bordered={false} bodyStyle={{ padding: "24px" }}>
        {!isMaximized && (
          <div className="calendar-controls">
            <Row justify="space-between" align="middle" style={{ marginBottom: "24px" }}>
              <Col>
                <PageTitle icon={<CalendarDays />} title="Calendario de Actividades" />
              </Col>
              {auth.Rol === "director" && (
                <Col>
                  <Select
                    value={selectedUser}
                    onChange={(value) => setSelectedUser(value)}
                    style={{ width: 280 }}
                    placeholder="Filtrar por usuario"
                    suffixIcon={<User size={18} />}
                    size="large"
                  >
                    <Select.Option value="all">Todos los usuarios</Select.Option>
                    {users.map((user) => (
                      <Select.Option key={user.UsuarioID} value={user.UsuarioID}>
                        {user.Nombre} {user.Apellido}
                      </Select.Option>
                    ))}
                  </Select>
                </Col>
              )}
            </Row>
            <CalendarLegend
              visibleTypes={visibleTypes}
              handleLegendClick={handleLegendClick}
              showAllEventTypes={showAllEventTypes}
              hideAllEventTypes={hideAllEventTypes}
            />
          </div>
        )}

        <Spin spinning={loading} size="large">
          <ConfigProvider locale={customLocale}>
            <Calendar
              fullscreen={true}
              dateCellRender={(date) => (
                <CalendarCell value={date} dates={dates} visibleTypes={visibleTypes} handleItemClick={handleItemClick} />
              )}
              headerRender={(props) => <CalendarHeader {...props} isMaximized={isMaximized} toggleMaximize={toggleMaximize} />}
              onSelect={onSelect}
              onPanelChange={onPanelChange}
              className={`enhanced-calendar ${isMaximized ? "calendar-maximized" : ""}`}
            />
          </ConfigProvider>
        </Spin>
      </Card>

      <ItemDetailsModal
        visible={itemModalVisible}
        onCancel={() => setItemModalVisible(false)}
        item={selectedItem}
        zIndex={isMaximized ? 10001 : undefined}
      />

      <DayDetailsModal
        visible={dayModalVisible}
        onCancel={() => setDayModalVisible(false)}
        day={selectedDay}
        items={selectedDayItems}
        zIndex={isMaximized ? 10001 : undefined}
      />
    </div>
  );
}
